import { useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon, Clock } from "lucide-react";

import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";

function pad(n: number) {
  return String(n).padStart(2, "0");
}

const HOURS = Array.from({ length: 24 }, (_, i) => pad(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad(i));

interface DateFieldProps {
  value: Date | undefined;
  onChange: (d: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function DateField({
  value,
  onChange,
  placeholder = "Selecciona fecha",
  disabled,
  className,
}: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const valid = value && !isNaN(value.getTime()) ? value : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal",
            !valid && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {valid
            ? format(valid, "d 'de' MMMM 'de' yyyy", { locale: es })
            : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={valid}
          onSelect={(d) => {
            onChange(d ?? undefined);
            setOpen(false);
          }}
          locale={es}
        />
      </PopoverContent>
    </Popover>
  );
}

interface TimeFieldProps {
  hour: string;
  minute: string;
  onChange: (hour: string, minute: string) => void;
  disabled?: boolean;
  className?: string;
}

export function TimeField({
  hour,
  minute,
  onChange,
  disabled,
  className,
}: TimeFieldProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Clock className="h-4 w-4 text-gray-500" />
      <Select
        value={hour}
        onValueChange={(v) => onChange(v, minute)}
        disabled={disabled}
      >
        <SelectTrigger className="w-[78px]">
          <SelectValue placeholder="HH" />
        </SelectTrigger>
        <SelectContent>
          {HOURS.map((h) => (
            <SelectItem key={h} value={h}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-gray-500">:</span>
      <Select
        value={minute}
        onValueChange={(v) => onChange(hour, v)}
        disabled={disabled}
      >
        <SelectTrigger className="w-[78px]">
          <SelectValue placeholder="mm" />
        </SelectTrigger>
        <SelectContent>
          {MINUTES.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function formatDateTimeForApi(
  date: Date,
  hour: string,
  minute: string,
): string {
  const y = date.getFullYear();
  const mo = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  return `${y}-${mo}-${d} ${hour}:${minute}:00`;
}